import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Search, ShoppingBag, Bed, Clock, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

const CATEGORY_CHIPS = [
  { key: 'laptop',     label: 'Laptop',        emoji: '💻', query: 'Laptop for coding under 80000',                 kind: 'product' },
  { key: 'phone',      label: 'Phone',         emoji: '📱', query: 'Android phone under 30000 for daily use',       kind: 'product' },
  { key: 'headphones', label: 'Headphones',    emoji: '🎧', query: 'Noise cancelling headphones under 10000',       kind: 'product' },
  { key: 'tv',         label: 'TV',            emoji: '📺', query: '55 inch 4K TV under 50000',                     kind: 'product' },
  { key: 'ac',         label: 'AC',            emoji: '❄️', query: '1.5 ton inverter split AC under 45000',         kind: 'product' },
  { key: 'stay_goa',   label: 'Stay: Goa',     emoji: '🏖️', query: 'Weekend in North Goa for 2 adults under 6000/night', kind: 'stay' },
];

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function SmartBuy() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [query, setQuery]   = useState('');
  const [budget, setBudget] = useState('');
  const [kind, setKind]     = useState('product');
  const [loading, setLoading] = useState(false);
  const [recent, setRecent]   = useState([]);
  const [result, setResult]   = useState(null);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/research/recent?limit=8`, { headers });
      setRecent(res.data?.searches || []);
    } catch {}
  }, [token]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Poll while pending rows exist
  useEffect(() => {
    if (!recent.some(r => r.status === 'pending')) return;
    const id = setInterval(loadRecent, 6000);
    return () => clearInterval(id);
  }, [recent, loadRecent]);

  const openRecent = useCallback(async (r) => {
    if (r.status === 'pending') { toast.info("Chanakya is still researching — we'll notify you when ready."); return; }
    if (r.status === 'failed')  { toast.error("That review didn't land. Try searching again."); return; }
    try {
      const res = await axios.get(`${API}/research/${r.id}`, { headers });
      const row = res.data;
      setResult({ kind: row.kind, query: row.query, products: Array.isArray(row.products) ? row.products : [] });
      setQuery(row.query);
    } catch { toast.error("Couldn't open that review."); }
  }, [token]);

  const runResearch = useCallback(async (q) => {
    const finalQuery = (q ?? query).trim();
    if (finalQuery.length < 3) { toast.error('Type what you\'re shopping for'); return; }
    setLoading(true);
    setResult(null);
    try {
      await axios.post(`${API}/research/product`, {
        query: finalQuery,
        budget: parseFloat(budget) || 0,
        kind,
      }, { headers, timeout: 15000 });
      toast.success('Chanakya is researching — we\'ll notify you when ready');
      setQuery(''); setBudget('');
      loadRecent();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = status === 429 ? 'Slow down — wait a minute and try again.'
                : status === 502 ? 'Research service is briefly offline.'
                : status === 400 ? (detail || 'Add more detail to your search.')
                : (detail || 'Couldn\'t queue your query. Try again.');
      toast.error(msg);
      loadRecent();
    } finally { setLoading(false); }
  }, [query, budget, kind, token, loadRecent]);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

        {/* Hero */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-orange-100 rounded-2xl flex items-center justify-center">
              <ShoppingBag size={20} className="text-orange-500" />
            </div>
            <h1 className="text-2xl font-extrabold text-stone-900 dark:text-stone-100">Smart Buy</h1>
          </div>
          <p className="text-sm text-stone-500 dark:text-stone-400 ml-[52px]">
            Real reviews from Reddit, YouTube & Amazon — then Chanakya tells you what to buy.
          </p>
        </div>

        {/* Recent research */}
        {recent.length > 0 && (
          <div>
            <p className="text-[11px] font-bold tracking-widest text-stone-400 uppercase mb-3">Recent Research</p>
            <div className="space-y-2">
              {recent.slice(0, 5).map(r => (
                <button
                  key={r.id}
                  onClick={() => openRecent(r)}
                  className="w-full flex items-center gap-3 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl px-4 py-3 text-left hover:border-orange-300 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
                    {r.status === 'pending' ? (
                      <RefreshCw size={13} className="text-orange-500 animate-spin" />
                    ) : (
                      r.kind === 'stay'
                        ? <Bed size={13} className={r.status === 'failed' ? 'text-red-400' : 'text-orange-500'} />
                        : <ShoppingBag size={13} className={r.status === 'failed' ? 'text-red-400' : 'text-orange-500'} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 truncate">{r.query}</p>
                    <p className="text-xs text-stone-400 mt-0.5 truncate">
                      {r.status === 'pending' ? 'Chanakya is analysing…'
                        : r.status === 'failed' ? 'Didn\'t land — click to retry'
                        : r.summary_preview || ''}
                    </p>
                  </div>
                  <span className="text-[10px] text-stone-400 shrink-0">{timeAgo(r.created_at)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category chips */}
        <div>
          <p className="text-[11px] font-bold tracking-widest text-stone-400 uppercase mb-3">Popular Searches</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_CHIPS.map(chip => (
              <button
                key={chip.key}
                onClick={() => { setKind(chip.kind); setQuery(chip.query); runResearch(chip.query); }}
                className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl text-sm font-medium text-stone-700 dark:text-stone-300 hover:border-orange-300 transition-colors"
              >
                <span>{chip.emoji}</span>{chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search form */}
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-5 space-y-4">
          {/* Kind toggle */}
          <div className="flex gap-2">
            {['product', 'stay'].map(k => (
              <button key={k} onClick={() => setKind(k)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors flex items-center justify-center gap-2
                  ${kind === k ? 'bg-orange-500 border-orange-500 text-white' : 'border-stone-200 dark:border-stone-700 text-stone-500 dark:text-stone-400'}`}>
                {k === 'product' ? <><ShoppingBag size={13} /> Product</> : <><Bed size={13} /> Stay</>}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide">What are you looking for?</label>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runResearch()}
              placeholder={kind === 'product' ? 'Laptop for coding under 80k' : 'Weekend in Goa for 2, under 6k/night'}
              className="w-full h-10 px-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-sm text-stone-900 dark:text-stone-100 outline-none focus:border-orange-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide">Budget (₹) — optional</label>
            <input
              value={budget}
              onChange={e => setBudget(e.target.value)}
              placeholder={kind === 'product' ? '80000' : '6000/night'}
              type="number"
              className="w-full h-10 px-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-sm text-stone-900 dark:text-stone-100 outline-none focus:border-orange-400"
            />
          </div>

          <button
            onClick={() => runResearch()}
            disabled={loading || query.trim().length < 3}
            className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <RefreshCw size={15} className="animate-spin" /> : <Search size={15} />}
            {loading ? 'Queuing research…' : 'Find my picks'}
          </button>

          <p className="text-center text-xs text-stone-400">
            Takes 10–20 seconds. Chanakya pulls genuine reviews, not a product catalogue.
          </p>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100 dark:border-stone-800 bg-orange-50 dark:bg-orange-950/30">
              <p className="text-[11px] font-bold tracking-widest text-orange-500 uppercase">Chanakya's Picks</p>
              <p className="text-base font-extrabold text-stone-900 dark:text-stone-100 mt-0.5">{result.query}</p>
            </div>

            {result.products?.length > 0 ? (
              <div className="divide-y divide-stone-100 dark:divide-stone-800">
                {result.products.slice(0, 5).map((p, i) => (
                  <div key={i} className="px-5 py-4">
                    <p className="font-extrabold text-stone-900 dark:text-stone-100 mb-1">{p.name}</p>
                    {p.why && (
                      <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed mb-3">
                        <span className="font-semibold text-orange-600 dark:text-orange-400">Why Chanakya picks this · </span>
                        {p.why}
                      </p>
                    )}
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg transition-colors">
                        <ExternalLink size={12} /> Open link
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-stone-500 dark:text-stone-400 mb-1">
                  This is an older research result — no structured picks available.
                </p>
                <p className="text-xs text-stone-400 mb-4">Search again to get Chanakya's latest picks.</p>
                <button
                  onClick={() => { setQuery(result.query); setResult(null); }}
                  className="inline-flex items-center gap-2 text-sm font-bold text-orange-500 border border-orange-200 px-4 py-2 rounded-xl hover:bg-orange-50 transition-colors"
                >
                  <Search size={13} /> Re-search "{result.query}"
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
